// HIZLI SERİ GİRİŞ — SAF MANTIĞIN TEK KAYNAĞI
// Spec: docs/superpowers/specs/2026-11-04-hizli-seri-giris-sepet-design.md
//
// KURALLAR (kullanıcı kararları 04-05.11.2026):
// - Ekleme sonrası YALNIZ EN (widthCm) silinir; BOY ve diğer tüm alanlar korunur;
//   adet 1'e döner (yanlış adetle sipariş riskini önler).
// - Ctrl+K = föyde satırı kaydet + yeni kalem (Enter'a İŞ YÜKLENMEZ — karışıklık
//   olmasın); Ctrl+F = arama; kısayollar kelimenin ilk harfi kuralında.
// - focusMeasureField ekleme sonrası imleci EN kutusuna döndürür (seri giriş hızı).

/**
 * Sepete ekleme sonrası taslağı sıfırla — yalnız EN silinir.
 * @param {object} draftItem
 * @returns {object} yeni obje (girdi mutasyona uğramaz)
 */
export function resetDraftAfterAdd(draftItem) {
  return { ...(draftItem || {}), widthCm: "", qty: 1 };
}

/**
 * Kalemi çoğalt — tüm alanları kopyalayan YENİ obje döner.
 * @param {object} item
 * @returns {object}
 */
export function duplicateItem(item) {
  return { ...(item || {}) };
}

/**
 * Föyde "satırı kaydet + yeni kalem" kısayolu mu?
 * Yalnız (Ctrl|Cmd)+K. Enter ASLA true dönmez.
 * Hedef kuralı: TEXTAREA → false; serbest metin input'ları yalnız ölçü/adet/fiyat
 * test-id'liyse true; sayısal input ve input olmayan odaklar (body, buton) → true
 * (föy açıkken birincil aksiyon satırı kaydetmektir — koruma metin/not kutularında).
 * @param {KeyboardEvent} e
 * @param {Element} [target]
 */
export function isQuickAddKey(e, target) {
  if (!e || !(e.ctrlKey || e.metaKey)) return false;
  if (String(e.key).toLowerCase() !== "k") return false;
  if (!target) return true;
  if (target.tagName === "TEXTAREA") return false;
  if (target.tagName === "INPUT") {
    const type = String(target.type || "text").toLowerCase();
    if (type === "number") return true;
    const testid = target.getAttribute && target.getAttribute("data-testid");
    return /width|height|qty|price|adet|fiyat/i.test(testid || "");
  }
  return true;
}

/** Genel kaydet kısayolları: Ctrl+K, Ctrl+S, Ctrl+Enter (eski kısayollar korunur). */
export function isSaveShortcut(e) {
  if (!e || !(e.ctrlKey || e.metaKey)) return false;
  const k = String(e.key).toLowerCase();
  return k === "k" || k === "s" || k === "enter";
}

/** Arama kısayolu: Ctrl+F. */
export function isSearchShortcut(e) {
  return Boolean(e) && Boolean(e.ctrlKey || e.metaKey) && String(e.key).toLowerCase() === "f";
}

/** Çoğaltma kısayolu: Ctrl+D (tarayıcı yer imini engelleme çağırandadır). */
export function isDuplicateShortcut(e) {
  return Boolean(e) && Boolean(e.ctrlKey || e.metaKey) && String(e.key).toLowerCase() === "d";
}

/**
 * Yeni eklenen satırın vurgu sınıfı — 2 sn "fresh", sonra boş.
 * @param {number} addedAt epoch ms
 * @param {number} now epoch ms
 * @returns {string}
 */
export function freshHighlightClass(addedAt, now) {
  return now - addedAt < 2000 ? "fresh" : "";
}

/**
 * İmleci ölçü alanına döndür: önce [data-testid="pf-width"], yoksa ilk sayısal input.
 * @param {Document|Element} [root]
 * @returns {boolean} odaklandı mı
 */
export function focusMeasureField(root = (typeof document !== "undefined" ? document : null)) {
  if (!root || !root.querySelector) return false;
  const preferred = root.querySelector('[data-testid="pf-width"], [data-testid$="-width"]');
  const el = preferred || root.querySelector('input[type="number"]');
  if (!el || typeof el.focus !== "function") return false;
  el.focus();
  return true;
}

// Föy "Sepete Ekle" düğmelerinin test-id'leri (düğmenin kendi mantığı = validation +
// düzenleme modu — Ctrl+K aynen bu düğmeyi tıklar, ikinci bir yol açılmaz).
const ADD_BUTTON_SELECTOR = [
  '[data-testid="pf-add"]',
  '[data-testid="sadece-kumas-add"]',
  '[data-testid="stor-sistem-add"]',
  '[data-testid="dikey-ray-add"]',
  '[data-testid="metal-jaluzi-add"]',
].join(",");

/**
 * Ctrl+K'yı föyün birincil aksiyonuna bağla: föyün "Sepete Ekle / Kalemi Güncelle"
 * düğmesini bulur, disabled değilse tıklar. Düğme yoksa false (çağıran müdahale etmez).
 * @param {KeyboardEvent} e
 * @param {Document|Element} [root]
 * @returns {boolean} tuş yutuldu mu (düğme bulundu mu)
 */
export function triggerQuickAdd(e, root = (typeof document !== "undefined" ? document : null)) {
  if (!isQuickAddKey(e, e && e.target)) return false;
  const btn = root && root.querySelector ? root.querySelector(ADD_BUTTON_SELECTOR) : null;
  if (!btn) return false;
  if (e && typeof e.preventDefault === "function") e.preventDefault();
  // KRİTİK (05.11 incelemesi): olayı yut — global "Kaydet" dinleyicisi (Shortcuts.jsx)
  // aynı Ctrl+K'yı yakalayıp tüm teklifi de kaydediyordu (çift tetikleme).
  if (e && typeof e.stopPropagation === "function") e.stopPropagation();
  if (!btn.disabled && typeof btn.click === "function") btn.click();
  return true;
}

/**
 * Çoğaltma hedefi: düzenleme modunda DÜZENLENEN kalem, değilse son kalem.
 * @param {number|null} editingIndex
 * @param {number} length
 * @returns {number} hedef indeks (boş listede -1)
 */
export function duplicateTargetIndex(editingIndex, length) {
  if (editingIndex != null && editingIndex >= 0 && editingIndex < length) return editingIndex;
  return length - 1;
}

/**
 * Metin girişi yapılan odak mı? (textarea veya text input) — Ctrl+D gibi veri
 * değiştiren kısayollar buralarda çalıştırılmaz (yanlışlıkla sepet verisi).
 */
export function isTextEntryTarget(target) {
  if (!target || target.tagName === "TEXTAREA") return Boolean(target);
  if (target.tagName === "INPUT") {
    return String(target.type || "text").toLowerCase() !== "number";
  }
  return false;
}

/**
 * Canlı önizleme satırı görünsün mü? Yalnız YENİ kalem girişinde — düzenleme
 * modunda gerçek satır zaten listededir (hayalet satır olmasın, 05.11 incelemesi).
 */
export function showPreviewRow({ draftItem, activeGroup, drawerOpen, editingIndex }) {
  return Boolean(draftItem && activeGroup && drawerOpen) && editingIndex == null;
}

/**
 * Yeni satır için otomatik kaydırma kararı: kullanıcı zaten listenin alt
 * yakınındaysa kaydır; yukarıları inceliyorsa ELLEME (liste kullanıcıyı
 * aşağı çekmesin — 05.11 geri bildirimi).
 * @returns {boolean}
 */
export function shouldAutoScroll(scrollTop, scrollHeight, clientHeight, threshold = 150) {
  const distanceFromBottom = scrollHeight - clientHeight - scrollTop;
  return distanceFromBottom <= threshold;
}

/**
 * Taslak geri getirme şeridi görünsün mü? Yalnız EL DEĞMEMİŞ yeni oturumda
 * (sayfa açılışında taslak bulunduğunda). Kullanıcı çalışmaya başlayınca
 * (kalem eklendi / müşteri seçildi) veya teklif düzenlenirken ASLA görünmez —
 * 05.11 geri bildirimi: "teklif içindeyken çıkmasın".
 */
export function showDraftRestoreBanner({ showBanner, pendingDraft, itemsCount, customerId, isEdit, saveAsOrder }) {
  if (!showBanner || !pendingDraft) return false;
  if (isEdit || saveAsOrder) return false;
  return !itemsCount && !customerId;
}

/**
 * Önizleme sığdırma katsayısı: sayfa GENİŞLİĞE sığdırılır (yan boşluklar
 * dolar — 05.11 geri bildirimi). 0.4–3 aralığında kilitlenir.
 * @param {number} containerWidth kullanılabilir genişlik (px)
 * @param {number} [pageWidth] A4 = 794px (96dpi)
 * @param {number} [pad] toplam yatay dolgu (px)
 */
export function fitZoomFor(containerWidth, pageWidth = 794, pad = 32) {
  const raw = (Number(containerWidth) - pad) / pageWidth;
  if (!Number.isFinite(raw)) return 1;
  return Math.min(3, Math.max(0.4, raw));
}

/**
 * Adet değişimi — orantılı ölçekleme (düşüş yolu; motor/reçete fiyat akışı
 * bulunamazsa kullanılır). qty en az 1.
 */
export function scaleItemQty(item, newQty) {
  const oldQ = Number(item?.qty) || 1;
  const q = Math.max(1, Number(newQty) || 1);
  const ratio = q / oldQ;
  const r2 = (x) => Math.round(x * 100) / 100;
  return {
    ...item,
    qty: q,
    lineTotal: r2((Number(item?.lineTotal) || 0) * ratio),
    ...(item?.listLineTotal != null ? { listLineTotal: r2(Number(item.listLineTotal) * ratio) } : {}),
  };
}
